import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  FilePlus, 
  Settings, 
  Mail, 
  LogOut,
  Sun,
  Moon,
  Home,
  User,
  HardDrive,
  Globe,
  ListOrdered
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSite } from '../../contexts/SiteContext';
import { Toaster } from '../ui/sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const navItems = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { href: '/admin/posts', label: 'Posts', icon: FileText },
  { href: '/admin/pages', label: 'Pages', icon: FilePlus },
  { href: '/admin/menu-order', label: 'Menu Order', icon: ListOrdered },
  { href: '/admin/messages', label: 'Messages', icon: Mail },
];

const settingsItems = [
  { href: '/admin/settings', label: 'Site Settings', icon: Settings, exact: true },
  { href: '/admin/settings/email', label: 'Email', icon: Globe },
  { href: '/admin/settings/account', label: 'Account', icon: User },
  { href: '/admin/settings/backup', label: 'Backup', icon: HardDrive },
];

function NavLink({ href, label, icon: Icon, active, testId }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={href}
          data-testid={testId}
          className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors md:justify-start justify-center ${
            active
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Icon className="h-4 w-4 flex-shrink-0" />
          <span className="hidden md:inline">{label}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" className="md:hidden">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function NavButton({ onClick, label, icon: Icon, testId, className = '' }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          data-testid={testId}
          className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full md:justify-start justify-center ${className}`}
        >
          <Icon className="h-4 w-4 flex-shrink-0" />
          <span className="hidden md:inline">{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="md:hidden">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { settings } = useSite();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (href, exact = false) => {
    if (exact) return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen flex">
        {/* Sidebar: w-16 on mobile, w-64 on md+ */}
        <aside className="w-16 md:w-64 bg-card border-r border-border flex flex-col transition-all duration-200">
          <div className="p-2 md:p-4 border-b border-border">
            <Link to="/admin" className="font-bold text-lg hidden md:block" data-testid="admin-logo">
              {settings.site_name}
            </Link>
            <Link to="/admin" className="md:hidden flex justify-center" data-testid="admin-logo-mobile">
              <LayoutDashboard className="h-5 w-5" />
            </Link>
            <p className="text-xs text-muted-foreground mt-1 hidden md:block">Admin Dashboard</p>
          </div>

          <nav className="flex-1 p-2 md:p-4" aria-label="Admin navigation">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.href}>
                  <NavLink
                    href={item.href}
                    label={item.label}
                    icon={item.icon}
                    active={isActive(item.href, item.exact)}
                    testId={`admin-nav-${item.label.toLowerCase()}`}
                  />
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-4 border-t border-border">
              <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:block">Settings</p>
              <ul className="space-y-1">
                {settingsItems.map((item) => (
                  <li key={item.href}>
                    <NavLink
                      href={item.href}
                      label={item.label}
                      icon={item.icon}
                      active={isActive(item.href, item.exact)}
                      testId={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          <div className="p-2 md:p-4 border-t border-border space-y-1 md:space-y-2">
            <NavLink
              href="/"
              label="View Site"
              icon={Home}
              active={false}
              testId="admin-view-site"
            />
            
            <NavButton
              onClick={toggleTheme}
              label={theme === 'light' ? 'Dark Mode' : 'Light Mode'}
              icon={theme === 'light' ? Moon : Sun}
              testId="admin-theme-toggle"
              className="text-muted-foreground hover:bg-muted hover:text-foreground"
            />

            <NavButton
              onClick={handleLogout}
              label="Logout"
              icon={LogOut}
              testId="admin-logout-btn"
              className="text-destructive hover:bg-destructive/10"
            />
          </div>

          <div className="p-2 md:p-4 border-t border-border">
            <p className="text-xs text-muted-foreground hidden md:block">Logged in as:</p>
            <p className="text-sm font-medium truncate hidden md:block">{user?.email}</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="md:hidden flex justify-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="md:hidden">
                {user?.email}
              </TooltipContent>
            </Tooltip>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>

        <Toaster position="bottom-right" />
      </div>
    </TooltipProvider>
  );
}
